import * as Constants from "../Data/Constants";
import * as HLF from "html-living-framework";
import * as Topics from "Data/Topics";
import * as SFX from "Data/SoundEffect";
import * as VO from "Data/VoiceOver";

import { LayoutButton } from "GameObjects/UI/LayoutButton";
import { LayoutToggleButton } from "GameObjects/UI/LayoutToggleButton";
import { Panel } from "GameObjects/UI/Panel";
import Settings from "Data/Settings";
import { Slider } from "GameObjects/UI/Slider";
import { SettingsPopupLayout } from "Layouts/SettingsPopupLayout";
import { LayoutSprite } from "GameObjects/UI/LayoutSprite";
import { SettingsMenuTabButton } from "GameObjects/UI/SettingsMenuTabButton";
import { AccessibleLayoutSprite } from "GameObjects/UI/AccessibleLayoutSprite";
import { Application } from "Application";

export default class SettingsPopup extends HLF.Popup implements ReturnType<typeof SettingsPopupLayout> {

    public static readonly NAME = "SettingsPopup";
    protected static currentTab: number = 0;
    protected static _isOpen: boolean = false;

    public readonly doneButton: LayoutButton;
    public readonly soundAndCaptionSettingsButton: LayoutButton;
    public readonly gameplaySettingsButton: LayoutButton;
    public readonly displaySettingsButton: LayoutButton;
    public readonly switchControlsButton: LayoutButton;
    public readonly resetButton: LayoutButton;
    public readonly helpButton: LayoutButton;

    public readonly audioPanel: Panel;
    public readonly audioDescriptionsPanel: Panel;
    public readonly closedCaptioningPanel: Panel;
    public readonly difficultyPanel: Panel;
    public readonly flightSpeedPanel: Panel;
    public readonly textSizePanel: Panel;
    public readonly colorBlindPanel: Panel;
    public readonly reducedAnimationPanel: Panel;
    public readonly buttonSizePanel: Panel;
    public readonly contrastPanel: Panel;
    public readonly backgroundImagesPanel: Panel;
    public readonly controlsPanel: Panel;

    public readonly audioSliderBg: LayoutSprite[];
    public readonly audioDescriptionsToggleBG: LayoutSprite;
    public readonly closedCaptionsToggleBG: LayoutSprite;
    public readonly musicSliderIcon: LayoutSprite;
    public readonly soundEffectsSliderIcon: LayoutSprite;
    public readonly voiceoverSliderIcon: LayoutSprite;
    public readonly difficultySliderBg: LayoutSprite;
    public readonly difficultySliderLabel: LayoutSprite;
    public readonly flightSpeedSliderBg: LayoutSprite;
    public readonly flightSpeedSliderLabel: LayoutSprite;
    public readonly textSizeSliderBg: LayoutSprite;
    public readonly textSizeSliderLabel: LayoutSprite;
    public readonly buttonSizeSliderBg: LayoutSprite;
    public readonly buttonSizeSliderLabel: LayoutSprite;
    public readonly colorBlindSliderBg: LayoutSprite;
    public readonly colorblindSliderLabel: LayoutSprite;
    public readonly contrastSliderBg: LayoutSprite;
    public readonly contrastSliderLabel: LayoutSprite;
    public readonly reducedAnimationSliderBg: LayoutSprite;
    public readonly reducedAnimationSliderLabel: LayoutSprite;
    public readonly backgroundImagesSliderLabel: LayoutSprite;

    public readonly keyboardControlsSprite: AccessibleLayoutSprite;
    public readonly switchControlsSprite: AccessibleLayoutSprite[];
    public readonly gazeControlsSprite: AccessibleLayoutSprite;

    public readonly controlsPanelKeyboardTabBg: LayoutSprite;
    public readonly controlsPanelSwitchTabBg: LayoutSprite;
    public readonly controlsPanelGazeTabBg: LayoutSprite;
    public readonly controlsPanelKeyboardTabButton: LayoutButton;
    public readonly controlsPanelSwitchTabButton: LayoutButton;
    public readonly controlsPanelGazeTabButton: LayoutButton;


    protected _subscriptions: any[] = [];

    protected gameplaySettingsTab: PIXI.Container;
    protected soundAndCaptionSettingsTab: PIXI.Container;
    protected displaySettingsTab: PIXI.Container;
    protected controlsTab: PIXI.Container;

    protected audioDescriptionsToggleButton: LayoutToggleButton;
    protected closedCaptionsToggleButton: LayoutToggleButton;

    protected reducedAnimationSlider: Slider;
    protected backgroundImagesSlider: Slider;
    protected musicSlider: Slider;
    protected soundEffectsSlider: Slider;
    protected voiceoverSlider: Slider;
    protected textSizeSlider: Slider;
    protected buttonSizeSlider: Slider;
    protected difficultySlider: Slider;
    protected flightSpeedSlider: Slider;
    protected contrastSlider: Slider;
    protected colorBlindSlider: Slider;

    protected tabs: PIXI.Container[] = [];
    protected tabButtons: LayoutButton[] = []

    public constructor() {
        super();

        this._subscriptions.push(
            PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this)),
        );
        this.doneButton = this.addChild(new LayoutButton(this.onDoneButtonPressed.bind(this), VO.SETTINGS_DONE, SFX.UI_CLICK));
        this.resetButton = this.addChild(new LayoutButton(this.onResetButtonPressed.bind(this), VO.BUTTON_RESET_HOVER, SFX.UI_CLICK));
        this.helpButton = this.addChild(new LayoutButton(this.onHelpButtonPressed.bind(this), VO.BUTTON_HELP_HOVER, SFX.UI_CLICK));

        this.soundAndCaptionSettingsButton = this.addChild(new SettingsMenuTabButton(this.onSoundAndCaptionSettingsButtonPressed.bind(this), VO.SETTINGS_SOUND_TITLE, SFX.UI_CLICK));
        this.gameplaySettingsButton = this.addChild(new SettingsMenuTabButton(this.onGameplaySettingsButtonPressed.bind(this), VO.SETTINGS_GAMEPLAY_TITLE, SFX.UI_CLICK));
        this.displaySettingsButton = this.addChild(new SettingsMenuTabButton(this.onDisplaySettingsButtonPressed.bind(this), VO.SETTINGS_DISPLAY_TITLE, SFX.UI_CLICK));
        this.switchControlsButton = this.addChild(new SettingsMenuTabButton(this.onAccessInformationButtonPressed.bind(this), VO.SETTINGS_ACCESS_TITLE, SFX.UI_CLICK));

        this.soundAndCaptionSettingsButton.setDisabledImage(HLF.PixiUtils.getTexture("cyberchase-settings-btn-tab-sound-on"));
        this.gameplaySettingsButton.setDisabledImage(HLF.PixiUtils.getTexture("cyberchase-settings-btn-tab-gameplay-on"));
        this.displaySettingsButton.setDisabledImage(HLF.PixiUtils.getTexture("cyberchase-settings-btn-tab-display-on"));
        this.switchControlsButton.setDisabledImage(HLF.PixiUtils.getTexture("cyberchase-settings-btn-tab-controls-on"));

        this.tabButtons.push(this.soundAndCaptionSettingsButton);
        this.tabButtons.push(this.gameplaySettingsButton);
        this.tabButtons.push(this.displaySettingsButton);
        this.tabButtons.push(this.switchControlsButton);

        this.soundAndCaptionSettingsTab = this.addChild(new PIXI.Container());
        this.gameplaySettingsTab = this.addChild(new PIXI.Container());
        this.displaySettingsTab = this.addChild(new PIXI.Container());
        this.controlsTab = this.addChild(new PIXI.Container());

        this.tabs.push(this.soundAndCaptionSettingsTab);
        this.tabs.push(this.gameplaySettingsTab);
        this.tabs.push(this.displaySettingsTab);
        this.tabs.push(this.controlsTab);

// TAB 1: sound and caption settings
        this.audioPanel = this.soundAndCaptionSettingsTab.addChild(new Panel("Audio", VO.BUTTON_AUDIO_HOVER, VO.SETTINGS_AUDIO_HELP));
        this.audioDescriptionsPanel = this.soundAndCaptionSettingsTab.addChild(new Panel("Audio Descriptions", VO.SETTINGS_AD_HOVER, VO.SETTINGS_AD_HELP));
        this.closedCaptioningPanel = this.soundAndCaptionSettingsTab.addChild(new Panel("Closed Captions", VO.SETTINGS_CC_HOVER, VO.SETTINGS_CC_HELP));

        this.audioSliderBg = [
            this.soundAndCaptionSettingsTab.addChild(new LayoutSprite()),
            this.soundAndCaptionSettingsTab.addChild(new LayoutSprite()),
            this.soundAndCaptionSettingsTab.addChild(new LayoutSprite()),
        ];
        this.audioDescriptionsToggleBG = this.soundAndCaptionSettingsTab.addChild(new LayoutSprite());
        this.closedCaptionsToggleBG = this.soundAndCaptionSettingsTab.addChild(new LayoutSprite());
        this.musicSliderIcon = this.soundAndCaptionSettingsTab.addChild(new LayoutSprite());
        this.soundEffectsSliderIcon = this.soundAndCaptionSettingsTab.addChild(new LayoutSprite());
        this.voiceoverSliderIcon = this.soundAndCaptionSettingsTab.addChild(new LayoutSprite());

        this.musicSlider = this.audioSliderBg[0].addChild(new Slider(5, "cyberchase-settings-5notchbar", this.onMusicSliderValueChanged.bind(this), VO.SETTINGS_MUSIC_HOVER));
        this.soundEffectsSlider = this.audioSliderBg[1].addChild(new Slider(5, "cyberchase-settings-5notchbar", this.onSoundEffectsSliderValueChanged.bind(this), VO.SETTINGS_SFX_HOVER));
        this.voiceoverSlider = this.audioSliderBg[2].addChild(new Slider(5, "cyberchase-settings-5notchbar", this.onVoiceoverSliderValueChanged.bind(this), VO.SETTINGS_VO_HOVER));

        this.audioDescriptionsToggleButton = this.audioDescriptionsToggleBG.addChild(new LayoutToggleButton(this.onAudioDescriptionsToggleButtonPressed.bind(this), {on: VO.AD_ON, off: VO.AD_OFF}, SFX.UI_SELECT));
        this.closedCaptionsToggleButton = this.closedCaptionsToggleBG.addChild(new LayoutToggleButton(this.onCaptionsToggleButtonPressed.bind(this), {on: VO.CC_ON, off: VO.CC_OFF}, SFX.UI_SELECT));

// TAB 2: difficulty / gameplay settings
        this.difficultyPanel = this.gameplaySettingsTab.addChild(new Panel("Challenge Level", VO.SETTINGS_DIFFICULTY_HOVER, VO.SETTINGS_DIFFICULTY_HELP));
        this.difficultySliderBg = this.gameplaySettingsTab.addChild(new LayoutSprite());
        this.difficultySliderLabel = this.gameplaySettingsTab.addChild(new LayoutSprite());
        this.difficultySlider = this.difficultySliderBg.addChild(new Slider(4, "cyberchase-settings-4notchbar", this.onDifficultySliderValueChanged.bind(this), VO.SETTINGS_DIFFICULTY_HOVER));

        this.flightSpeedPanel = this.gameplaySettingsTab.addChild(new Panel("Flight Speed", VO.SETTINGS_SPEED_HOVER, VO.SETTINGS_SPEED_HELP));
        this.flightSpeedSliderBg = this.gameplaySettingsTab.addChild(new LayoutSprite());
        this.flightSpeedSliderLabel = this.gameplaySettingsTab.addChild(new LayoutSprite());
        this.flightSpeedSlider = this.flightSpeedSliderBg.addChild(new Slider(8, "cyberchase-settings-8notchbar", this.onFlightSpeedSliderValueChanged.bind(this), VO.SETTINGS_SPEED_HOVER));


// TAB 3: display settings

        this.textSizePanel = this.displaySettingsTab.addChild(new Panel("Text Size", VO.SETTINGS_TEXT_SIZE_HOVER, VO.SETTINGS_TEXT_SIZE_HELP));
        this.colorBlindPanel = this.displaySettingsTab.addChild(new Panel("Color", VO.SETTINGS_COLOR_HOVER, VO.SETTINGS_COLOR_HELP));
        this.reducedAnimationPanel = this.displaySettingsTab.addChild(new Panel("Animations", VO.SETTINGS_ANIMATIONS_HOVER, VO.SETTINGS_ANIMATIONS_HELP));
        this.buttonSizePanel = this.displaySettingsTab.addChild(new Panel("Button Size", VO.SETTINGS_BUTTON_SIZE_HOVER, VO.SETTINGS_BUTTON_SIZE_HELP));
        this.contrastPanel = this.displaySettingsTab.addChild(new Panel("Text Color", VO.SETTINGS_TEXT_COLOR_HOVER, VO.SETTINGS_TEXT_COLOR_HELP));
        this.backgroundImagesPanel = this.displaySettingsTab.addChild(new Panel("Backgrounds", VO.SETTINGS_BACKGROUNDS_HOVER, VO.SETTINGS_BACKGROUNDS_HELP));

        this.textSizeSliderBg = this.displaySettingsTab.addChild(new LayoutSprite());
        this.textSizeSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());
        this.buttonSizeSliderBg = this.displaySettingsTab.addChild(new LayoutSprite());
        this.buttonSizeSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());
        this.colorBlindSliderBg = this.displaySettingsTab.addChild(new LayoutSprite());
        this.colorblindSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());
        this.contrastSliderBg = this.displaySettingsTab.addChild(new LayoutSprite());
        this.contrastSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());
        this.reducedAnimationSliderBg = this.displaySettingsTab.addChild(new LayoutSprite());
        this.reducedAnimationSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());
        this.backgroundImagesSliderLabel = this.displaySettingsTab.addChild(new LayoutSprite());

        this.textSizeSlider = this.textSizeSliderBg.addChild(new Slider(3, "cyberchase-settings-3notchbar", this.onTextSizeSliderValueChanged.bind(this), VO.SETTINGS_TEXT_SIZE_HOVER));
        this.colorBlindSlider = this.colorBlindSliderBg.addChild(new Slider(2, "cyberchase-settings-2notchbar", this.onColorBlindSliderValueChanged.bind(this), VO.SETTINGS_COLOR_HOVER));
        this.reducedAnimationSlider = this.reducedAnimationSliderBg.addChild(new Slider(2, "cyberchase-settings-2notchbar", this.onReducedAnimationSliderValueChanged.bind(this), VO.SETTINGS_ANIMATIONS_HOVER));
        this.buttonSizeSlider = this.buttonSizeSliderBg.addChild(new Slider(2, "cyberchase-settings-2notchbar", this.onButtonSizeSliderValueChanged.bind(this), VO.SETTINGS_BUTTON_SIZE_HOVER));
        this.contrastSlider = this.contrastSliderBg.addChild(new Slider(2, "cyberchase-settings-2notchbar", this.onContrastSliderValueChanged.bind(this), VO.SETTINGS_TEXT_COLOR_HOVER));
        this.backgroundImagesSlider = this.backgroundImagesPanel.addChild(new Slider(2, "cyberchase-settings-2notchbar", this.onBackgroundImagesSliderValueChanged.bind(this), VO.SETTINGS_BACKGROUNDS_HOVER));

// TAB 4: controls
        this.controlsPanel = this.controlsTab.addChild(new Panel("Access Information", VO.SETTINGS_ACCESS_TITLE, VO.SETTINGS_ACCESS_HELP));
        this.controlsPanelKeyboardTabBg = this.controlsTab.addChild(new LayoutSprite());
        this.controlsPanelKeyboardTabButton = this.controlsTab.addChild(new LayoutButton(this.onKeyboardControlsButtonPressed.bind(this), VO.SETTINGS_KEYBOARD_HOVER, SFX.UI_CLICK));
        this.keyboardControlsSprite = this.controlsTab.addChild(new AccessibleLayoutSprite(VO.SETTINGS_KEYBOARD_SELECT));

        this.controlsPanelSwitchTabBg = this.controlsTab.addChild(new LayoutSprite());
        this.controlsPanelSwitchTabButton = this.controlsTab.addChild(new LayoutButton(this.onSwitchControlsButtonPressed.bind(this), [VO.SETTINGS_ACCESS_SWITCH_HOVER, VO.SETTINGS_ACCESS_SWITCH_HELP], SFX.UI_CLICK));
        this.switchControlsSprite = [
            this.controlsTab.addChild(new AccessibleLayoutSprite(VO.SETTINGS_KEYBOARD_SELECT)),
            this.controlsTab.addChild(new AccessibleLayoutSprite(VO.SETTINGS_KEYBOARD_SELECT)),
        ]

        this.controlsPanelGazeTabBg = this.controlsTab.addChild(new LayoutSprite());
        this.controlsPanelGazeTabButton = this.controlsTab.addChild(new LayoutButton(this.onGazeControlsButtonPressed.bind(this), VO.SETTINGS_ACCESS_HEAD_HOVER, SFX.UI_CLICK));
        this.gazeControlsSprite = this.controlsTab.addChild(new AccessibleLayoutSprite(VO.SETTINGS_ACCESS_HEAD_SELECT));

    }

    public static get isOpen(): boolean {
        return SettingsPopup._isOpen;
    }


    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set(-1024 / 2, -768 / 2);
    }

    public destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]): void {
        super.destroy(pOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    /** @inheritdoc */
    public init(pSize: PIXI.Point): void {
        super.init(pSize);
        SettingsPopupLayout.call(this);
        this.musicSlider.x = this.musicSliderIcon.width / 2;
        this.soundEffectsSlider.x = this.soundEffectsSliderIcon.width / 2;
        this.voiceoverSlider.x = this.voiceoverSliderIcon.width / 2;

        this.difficultySlider.y = this.difficultySliderLabel.height / 2;
        this.flightSpeedSlider.y = this.flightSpeedSliderLabel.height / 2;

        this.textSizeSlider.y = this.textSizeSliderLabel.height / 2;
        this.colorBlindSlider.y = this.colorblindSliderLabel.height / 2;
        this.reducedAnimationSlider.y = this.reducedAnimationSliderLabel.height / 2;
        this.buttonSizeSlider.y = this.buttonSizeSliderLabel.height / 2;
        this.contrastSlider.y = this.contrastSliderLabel.height / 2;
        this.backgroundImagesSlider.y = this.backgroundImagesSliderLabel.height;

        PubSub.publishSync(Topics.PAUSE_GAME, SettingsPopup.NAME);

        if (this.blackout) {
            this.blackout.beginFill(0x000000, 1);
            this.blackout.drawRect(0, 0, pSize.x, pSize.y);
            this.blackout.endFill();
        }
    }

    protected _hide() {
        super._hide();
        Application.instance.voiceover.stopVO();
        PubSub.publishSync(Topics.UNPAUSE_GAME, SettingsPopup.NAME);
    }

    protected AnimateIn(pCallback: () => void): void {
        Application.instance.voiceover.stopVO();

        SettingsPopup._isOpen = true;
        this.ShowTab(SettingsPopup.currentTab);
        this.updateDisplay();

        this.switchControlsSprite.forEach(s=>s.visible = false);
        this.controlsPanelSwitchTabBg.visible = false;

        this.gazeControlsSprite.visible = false;
        this.controlsPanelGazeTabBg.visible = false;

        pCallback();

        const focusables = [
            ...this.tabButtons,
// tab 1
            this.audioPanel.title,
            this.musicSlider,
            this.soundEffectsSlider,
            this.voiceoverSlider,
            this.audioDescriptionsPanel.title,
            this.audioDescriptionsToggleButton,
            this.closedCaptioningPanel.title,
            this.closedCaptionsToggleButton,
// tab 2
            this.difficultyPanel.title,
            this.difficultySlider,
            this.flightSpeedPanel.title,
            this.flightSpeedSlider,
// tab 3
            this.textSizePanel.title,
            this.textSizeSlider,
            this.colorBlindPanel.title,
            this.colorBlindSlider,
            this.reducedAnimationPanel.title,
            this.reducedAnimationSlider,
            this.buttonSizePanel.title,
            this.buttonSizeSlider,
            this.contrastPanel.title,
            this.contrastSlider,
            this.backgroundImagesPanel.title,
            this.backgroundImagesSlider,
// tab 4
            this.controlsPanel.title,
            this.controlsPanelKeyboardTabButton,
            this.keyboardControlsSprite,
            this.controlsPanelSwitchTabButton,
            ...this.switchControlsSprite,
            this.controlsPanelGazeTabButton,
            this.gazeControlsSprite,

            this.doneButton,
            this.helpButton,
            this.resetButton,
        ];

        this.keyboardControlsSprite.interactive = Settings.audioDescriptionEnabled;
        this.switchControlsSprite.forEach(s=>s.interactive = Settings.audioDescriptionEnabled);
        this.gazeControlsSprite.interactive = Settings.audioDescriptionEnabled;

        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, focusables);
        PubSub.publishSync(HLF.CLEAR_FOCUS);
        HLF.Application.instance.voiceover.playVO(this.tabButtons[SettingsPopup.currentTab].hoverVO);
    }

    protected AnimateOut(pCallback: () => void): void {
        SettingsPopup._isOpen = false;
        pCallback();
    }

    protected onDoneButtonPressed() {
        this.hide();
    }

    protected onHelpButtonPressed() {
        HLF.Application.instance.voiceover.playVO(VO.SETTINGS_HELP);
    }

    protected onResetButtonPressed() {
        Settings.audioDescriptionEnabled = false;
        Settings.buttonPosition = 0;
        Settings.buttonSizeMultiplier = Constants.BUTTON_SIZE_MIN;
        Settings.closedCaptionsEnabled = false;
        Settings.colorBlindMode = 0;
        Settings.contrast = 0.5;
        Settings.difficulty = 1 / 3;
        Settings.obstacleSpeed = 3 / 7;
        Settings.hideBackgroundImages = false;
        Settings.musicVolume = 1;
        Settings.moreAnimation = true;
        Settings.soundEffectsVolume = 1;
        Settings.textSizeMultiplier = 1.375;
        Settings.voiceoverVolume = 1;
    }

    protected ShowTab(pIndex: number) {
        for (let i = 0; i < this.tabs.length; i++) {
            const shouldTabBeActive = i === pIndex;
            this.tabs[i].visible = shouldTabBeActive;
            this.tabButtons[i].setInteractive(!shouldTabBeActive);
        }
        SettingsPopup.currentTab = pIndex;
        if (Settings.audioDescriptionEnabled) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SOUND_AUDIO_DESCRIPTION, HLF.PlayMode.Append);
        }
    }

    protected onSoundAndCaptionSettingsButtonPressed() {
        this.ShowTab(0);
        Application.instance.analytics.event("select_content", "Sound and Caption Settings");
    }

    protected onGameplaySettingsButtonPressed() {
        this.ShowTab(1);
        Application.instance.analytics.event("select_content", "Gameplay Settings");
    }

    protected onDisplaySettingsButtonPressed() {
        this.ShowTab(2);
        Application.instance.analytics.event("select_content", "Display Settings");
    }

    protected onAccessInformationButtonPressed() {
        this.ShowTab(3);
        Application.instance.analytics.event("select_content", "Access Information");
    }

    protected onKeyboardControlsButtonPressed() {
        this.switchControlsSprite.forEach(s=>s.visible = false);
        this.controlsPanelSwitchTabBg.visible = false;

        this.gazeControlsSprite.visible = false;
        this.controlsPanelGazeTabBg.visible = false;

        this.keyboardControlsSprite.visible = true;
        this.controlsPanelKeyboardTabBg.visible = true;

        HLF.Application.instance.voiceover.playVO(VO.SETTINGS_KEYBOARD_SELECT);
    }


    protected onSwitchControlsButtonPressed() {
        this.keyboardControlsSprite.visible = false;
        this.controlsPanelKeyboardTabBg.visible = false;

        this.gazeControlsSprite.visible = false;
        this.controlsPanelGazeTabBg.visible = false;

        this.switchControlsSprite.forEach(s=>s.visible = true);
        this.controlsPanelSwitchTabBg.visible = true;

        HLF.Application.instance.voiceover.playVO(VO.SETTINGS_ACCESS_SWITCH_SELECT);
    }

    protected onGazeControlsButtonPressed() {
        this.keyboardControlsSprite.visible = false;
        this.controlsPanelKeyboardTabBg.visible = false;

        this.switchControlsSprite.forEach(s=>s.visible = false);
        this.controlsPanelSwitchTabBg.visible = false;

        this.gazeControlsSprite.visible = true;
        this.controlsPanelGazeTabBg.visible = true;

        HLF.Application.instance.voiceover.playVO(VO.SETTINGS_ACCESS_HEAD_SELECT);
    }


    protected onAudioDescriptionsToggleButtonPressed() {
        Settings.audioDescriptionEnabled = !Settings.audioDescriptionEnabled;
        if (Settings.audioDescriptionEnabled) {
            HLF.Application.instance.voiceover.playVO(VO.AD_ON);
            Application.instance.analytics.event("select_content", "Audio Descriptions", 1);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.AD_OFF);
            Application.instance.analytics.event("select_content", "Audio Descriptions", 0);
        }
    }

    protected onCaptionsToggleButtonPressed() {
        Settings.closedCaptionsEnabled = !Settings.closedCaptionsEnabled;
        if (Settings.closedCaptionsEnabled) {
            HLF.Application.instance.voiceover.playVO(VO.CC_ON);
            Application.instance.analytics.event("select_content", "Closed Captions", 1);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.CC_OFF);
            Application.instance.analytics.event("select_content", "Closed Captions", 0);
        }
    }

    protected onBackgroundImagesSliderValueChanged(pValue: number) {
        Settings.hideBackgroundImages = pValue === 0;
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_BACKGROUNDS_1);
            Application.instance.analytics.event("select_content", "Backgrounds", 0);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_BACKGROUNDS_2);
            Application.instance.analytics.event("select_content", "Backgrounds", 1);
        }
    }

    protected onMusicSliderValueChanged(pValue: number) {
        Settings.musicVolume = pValue;
        const playDing = ()=>{
            const ding = HLF.Application.instance.audio.getAudioTrack(SFX.UI_CLICK, HLF.AudioCategory.BGM);
            if (ding !== undefined) {
                ding.stop();
                ding.play();
            }
            else {
                HLF.Application.instance.audio.play(SFX.UI_CLICK, 1, false, HLF.AudioCategory.BGM);
            }
        }

        if (pValue === 0) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_OFF);
            Application.instance.analytics.event("select_content", "music", pValue * 4);
        }
        else if (pValue === 1/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_1);
            Application.instance.analytics.event("select_content", "music", pValue * 4);
        }
        else if (pValue === 2/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_2);
            Application.instance.analytics.event("select_content", "music", pValue * 4);
        }
        else if (pValue === 3/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_3);
            Application.instance.analytics.event("select_content", "music", pValue * 4);
        }
        else if (pValue === 4/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_4);
            Application.instance.analytics.event("select_content", "music", pValue * 4);
        }
    }

    protected onSoundEffectsSliderValueChanged(pValue: number) {
        Settings.soundEffectsVolume = pValue;
        const playDing = ()=>{
            const ding = HLF.Application.instance.audio.getAudioTrack(SFX.UI_CLICK, HLF.AudioCategory.SFX);
            if (ding !== undefined) {
                ding.stop();
                ding.play();
            }
            else {
                HLF.Application.instance.audio.play(SFX.UI_CLICK, 1, false, HLF.AudioCategory.SFX);
            }
        }
        if (pValue === 0) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SFX_OFF);
            Application.instance.analytics.event("select_content", "SFX", pValue * 4);
        }
        else if (pValue === 1/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_1);
            Application.instance.analytics.event("select_content", "SFX", pValue * 4);
        }
        else if (pValue === 2/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_2);
            Application.instance.analytics.event("select_content", "SFX", pValue * 4);
        }
        else if (pValue === 3/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_3);
            Application.instance.analytics.event("select_content", "SFX", pValue * 4);
        }
        else if (pValue === 4/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_4);
            Application.instance.analytics.event("select_content", "SFX", pValue * 4);
        }
    }

    protected onVoiceoverSliderValueChanged(pValue: number) {
        Settings.voiceoverVolume = pValue;
        const playDing = ()=>{
            const ding = HLF.Application.instance.audio.getAudioTrack(SFX.UI_CLICK, HLF.AudioCategory.VO);
            if (ding !== undefined) {
                ding.stop();
                ding.play();
            }
            else {
                HLF.Application.instance.audio.play(SFX.UI_CLICK, 1, false, HLF.AudioCategory.VO);
            }
        }

        if (pValue === 0) {
            playDing();
            Application.instance.captions.playCaption(VO.SETTINGS_VO_OFF);
            const vo = HLF.Application.instance.audio.getAudioTrack(VO.SETTINGS_VO_OFF, HLF.AudioCategory.DEFAULT);
            if (vo !== undefined) {
                vo.stop();
                vo.setVolumeWithModifiers(0.25, 1, 1);
                vo.play();
            }
            else {
                HLF.Application.instance.audio.play(VO.SETTINGS_VO_OFF, 0.25, false, HLF.AudioCategory.DEFAULT);
            }
            Application.instance.analytics.event("select_content", "voiceover", pValue * 4);
        }
        else if (pValue === 1/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_1);
            Application.instance.analytics.event("select_content", "voiceover", pValue * 4);
        }
        else if (pValue === 2/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_2);
            Application.instance.analytics.event("select_content", "voiceover", pValue * 4);
        }
        else if (pValue === 3/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_3);
            Application.instance.analytics.event("select_content", "voiceover", pValue * 4);
        }
        else if (pValue === 4/4) {
            playDing();
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_MUSIC_4);
            Application.instance.analytics.event("select_content", "voiceover", pValue * 4);
        }
    }

    protected onTextSizeSliderValueChanged(pValue: number) {
        Settings.textSizeMultiplier = HLF.MathUtils.lerp(1, 1.75, pValue);
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_TEXT_SIZE_1);
            Application.instance.analytics.event("select_content", "Text Size", 1);
        }
        else if (pValue === 0.5) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_TEXT_SIZE_2);
            Application.instance.analytics.event("select_content", "Text Size", 2);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_TEXT_SIZE_3);
            Application.instance.analytics.event("select_content", "Text Size", 3);
        }
    }

    protected onButtonSizeSliderValueChanged(pValue: number) {
        Settings.buttonSizeMultiplier = HLF.MathUtils.lerp(Constants.BUTTON_SIZE_MIN, Constants.BUTTON_SIZE_MAX, pValue);
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_BUTTON_SIZE_1);
            Application.instance.analytics.event("select_content", "Button Size", 0);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_BUTTON_SIZE_2);
            Application.instance.analytics.event("select_content", "Button Size", 1);
        }
    }

    protected onDifficultySliderValueChanged(pValue: number) {
        Settings.difficulty = pValue;
        if (pValue < 1/3) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_DIFFICULTY_ADAPTIVE);
            Application.instance.analytics.event("select_content", "Challenge Level", 0);
        }
        else if (pValue < 2/3) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_DIFFICULTY_1);
            Settings.obstacleSpeed = 3/7;
            Application.instance.analytics.event("select_content", "Challenge Level", 1);
        }
        else if (pValue < 3/3) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_DIFFICULTY_2);
            Settings.obstacleSpeed = 4/7;
            Application.instance.analytics.event("select_content", "Challenge Level", 2);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_DIFFICULTY_3);
            Settings.obstacleSpeed = 5/7;
            Application.instance.analytics.event("select_content", "Challenge Level", 3);
        }
    }

    protected onFlightSpeedSliderValueChanged(pValue: number) {
        Settings.obstacleSpeed = pValue;
        if (pValue < 1/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_1);
            Application.instance.analytics.event("select_content", "Flight Speed", 1);
        }
        else if (pValue < 2/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_2);
            Application.instance.analytics.event("select_content", "Flight Speed", 2);
        }
        else if (pValue < 3/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_3);
            Application.instance.analytics.event("select_content", "Flight Speed", 3);
        }
        else if (pValue < 4/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_4);
            Application.instance.analytics.event("select_content", "Flight Speed", 4);
        }
        else if (pValue < 5/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_5);
            Application.instance.analytics.event("select_content", "Flight Speed", 5);
        }
        else if (pValue < 6/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_6);
            Application.instance.analytics.event("select_content", "Flight Speed", 6);
        }
        else if (pValue < 7/7) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_7);
            Application.instance.analytics.event("select_content", "Flight Speed", 7);
        }
        else {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_SPEED_8);
            Application.instance.analytics.event("select_content", "Flight Speed", 8);
        }
    }

    protected onReducedAnimationSliderValueChanged(pValue: number) {
        Settings.moreAnimation = pValue === 1;
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_ANIMATIONS_1);
            Application.instance.analytics.event("select_content", "Animations", 0);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_ANIMATIONS_2);
            Application.instance.analytics.event("select_content", "Animations", 1);
        }
    }

    protected onContrastSliderValueChanged(pValue: number) {
        Settings.contrast = pValue;
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_TEXT_COLOR_1);
            Application.instance.analytics.event("select_content", "Text Color", 0);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_TEXT_COLOR_2);
            Application.instance.analytics.event("select_content", "Text Color", 1);
        }
    }

    protected onColorBlindSliderValueChanged(pValue: number) {
        Settings.colorBlindMode = pValue;
        if (pValue === 0) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_COLOR_1);
            Application.instance.analytics.event("select_content", "Color", 0);
        }
        else if (pValue === 1) {
            HLF.Application.instance.voiceover.playVO(VO.SETTINGS_COLOR_3);
            Application.instance.analytics.event("select_content", "Color", 1);
        }
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        this.updateDisplay();
        this.keyboardControlsSprite.interactive = Settings.audioDescriptionEnabled;
        this.switchControlsSprite.forEach(s=>s.interactive = Settings.audioDescriptionEnabled);
        this.gazeControlsSprite.interactive = Settings.audioDescriptionEnabled;
    }

    protected updateDisplay() {
        this.audioDescriptionsToggleButton.setToggleValue(Settings.audioDescriptionEnabled === true);
        this.closedCaptionsToggleButton.setToggleValue(Settings.closedCaptionsEnabled === true);

        this.textSizeSlider.setValue((Settings.textSizeMultiplier - 1) / (1.75 - 1));
        this.buttonSizeSlider.setValue((Settings.buttonSizeMultiplier - Constants.BUTTON_SIZE_MIN) / (Constants.BUTTON_SIZE_MAX - Constants.BUTTON_SIZE_MIN));
        this.reducedAnimationSlider.setValue(Settings.moreAnimation === true ? 1 : 0);
        this.backgroundImagesSlider.setValue(Settings.hideBackgroundImages === false ? 1 : 0);
        this.contrastSlider.setValue(Settings.contrast);
        this.colorBlindSlider.setValue(Settings.colorBlindMode);

        this.musicSlider.setValue(Settings.musicVolume);
        this.soundEffectsSlider.setValue(Settings.soundEffectsVolume);
        this.voiceoverSlider.setValue(Settings.voiceoverVolume);

        this.difficultySlider.setValue(Settings.difficulty);
        this.flightSpeedSlider.setValue(Settings.obstacleSpeed);
    }
}
